import { keepPreviousData, useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";

import { supabase } from "../../../data/supabase/client";
import { getTimeRangeFilter } from "../../../utils/timerange";

interface FilterOptions {
  users: string[];
  protocols: string[];
}

interface UseCoachingArtifactFilterOptionsParams {
  tenantId: string | null;
  timeRange: string;
  timezone: string;
  permittedEmails: string[] | null;
  enabled?: boolean;
}

export const useCoachingArtifactFilterOptions = ({
  tenantId,
  timeRange,
  timezone,
  permittedEmails,
  enabled = true
}: UseCoachingArtifactFilterOptionsParams) => {
  const { startDate: filterStart, endDate: filterEnd } = getTimeRangeFilter(timeRange, timezone);

  const results = useQueries({
    queries: [
      {
        queryKey: ["coaching-artifacts-users", tenantId, timeRange, permittedEmails],
        queryFn: async () => {
          let query = supabase
            .from("coaching_artifact")
            .select("agent_email")
            .eq("tenant_id", tenantId as string)
            .is("deleted", null)
            .lte("start_date", filterEnd)
            .gte("end_date", filterStart);

          if (permittedEmails && permittedEmails.length > 0) {
            query = query.in("agent_email", permittedEmails.map(email => email.toLowerCase()));
          }

          const { data, error } = await query;
          if (error) {
            throw error;
          }
          return [...new Set(data?.map(d => d.agent_email) || [])].sort();
        },
        enabled
      },
      {
        queryKey: ["coaching-artifacts-lenses", tenantId, timeRange, permittedEmails],
        queryFn: async () => {
          let query = supabase
            .from("coaching_artifact")
            .select("lens_name")
            .eq("tenant_id", tenantId as string)
            .is("deleted", null)
            .lte("start_date", filterEnd)
            .gte("end_date", filterStart);

          if (permittedEmails && permittedEmails.length > 0) {
            query = query.in("agent_email", permittedEmails.map(email => email.toLowerCase()));
          }

          const { data, error } = await query;
          if (error) {
            throw error;
          }
          return [...new Set(data?.map(d => d.lens_name) || [])].sort();
        },
        enabled
      }
    ]
  });

  const [usersResult, lensesResult] = results;

  return {
    data: {
      users: usersResult.data || [],
      protocols: lensesResult.data || []
    } as FilterOptions,
    isLoading: usersResult.isLoading || lensesResult.isLoading,
    error: usersResult.error || lensesResult.error
  };
};

export interface LensConfig {
  temperature: number;
  maxTokens: number;
  model: string;
  selectedAttributes: string[];
  includeAttributeStats: boolean;
  includeLensDefinition: boolean;
  includeTeamBestPractices: boolean;
  customPrompt: string;
}

export type CoachingArtifactStatus = "generating" | "created" | "failed";

export interface CoachingArtifact {
  id: string;
  tenant_id: string;
  agent_email: string;
  start_date: string;
  end_date: string;
  lens_name: string;
  lens_config: LensConfig;
  system_prompt: string | null;
  content: any;
  source_data_filter: {
    timeRange: string;
  };
  created_by: string | null;
  created: string;
  modified: string;
  deleted: string | null;
  status: CoachingArtifactStatus;
  job_id: string | null;
}

export type OrderBy = "title" | "created" | "start_date" | "agent_email" | "lens_name";
export type OrderDirection = "asc" | "desc";

interface UseCoachingArtifactsParams {
  tenantId: string | null;
  timeRange: string;
  timezone: string;
  permittedEmails: string[] | null;
  enabled?: boolean;
  page?: number;
  pageSize?: number;
  orderBy?: OrderBy;
  orderDirection?: OrderDirection;
  userFilter?: string;
  lensFilter?: string;
}

export interface PaginatedCoachingArtifacts {
  data: CoachingArtifact[];
  totalCount: number;
}

const GENERATING_POLL_INTERVAL_MS = 5_000;

export const useCoachingArtifacts = ({
  tenantId,
  timeRange,
  timezone,
  permittedEmails,
  enabled = true,
  page = 0,
  pageSize = 10,
  orderBy = "created",
  orderDirection = "desc",
  userFilter,
  lensFilter
}: UseCoachingArtifactsParams) => {
  return useQuery({
    queryKey: [
      "coaching-artifacts",
      tenantId,
      timeRange,
      permittedEmails,
      page,
      pageSize,
      orderBy,
      orderDirection,
      userFilter,
      lensFilter
    ],
    queryFn: async (): Promise<PaginatedCoachingArtifacts> => {
      const { startDate: filterStart, endDate: filterEnd } = getTimeRangeFilter(timeRange, timezone);

      let query = supabase
        .from("coaching_artifact")
        .select("*", { count: "exact" })
        .eq("tenant_id", tenantId as string)
        .is("deleted", null)
        .lte("start_date", filterEnd)
        .gte("end_date", filterStart);

      if (permittedEmails && permittedEmails.length > 0) {
        query = query.in("agent_email", permittedEmails.map(email => email.toLowerCase()));
      }

      if (userFilter && userFilter !== "all") {
        query = query.eq("agent_email", userFilter);
      }

      if (lensFilter && lensFilter !== "all") {
        query = query.eq("lens_name", lensFilter);
      }

      const orderColumn = orderBy === "title" ? "content->>title" : orderBy;
      const from = page * pageSize;
      const to = from + pageSize - 1;

      const { data, error, count } = await query
        .order(orderColumn, { ascending: orderDirection === "asc" })
        .range(from, to);

      if (error) {
        throw error;
      }

      const artifacts = (data ?? []) as unknown as CoachingArtifact[];

      return {
        data: artifacts,
        totalCount: count ?? 0
      };
    },
    enabled,
    placeholderData: keepPreviousData,
    refetchInterval: (query) => {
      const result = query.state.data as PaginatedCoachingArtifacts | undefined;
      if (!result) {
        return false;
      }
      return result.data.some((a) => a.status === "generating")
        ? GENERATING_POLL_INTERVAL_MS
        : false;
    }
  });
};

interface DeleteCoachingArtifactParams {
  artifactId: string;
  tenantId: string;
}

export const useDeleteCoachingArtifact = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ artifactId, tenantId }: DeleteCoachingArtifactParams) => {
      const { error } = await supabase
        .from("coaching_artifact")
        .update({ deleted: new Date().toISOString() })
        .eq("id", artifactId)
        .eq("tenant_id", tenantId)
        .in("status", ["generating", "failed"])
        .is("deleted", null);

      if (error) {
        throw error;
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        predicate: (query) => {
          const root = query.queryKey[0];
          return typeof root === "string" && root.startsWith("coaching-artifacts");
        }
      });
    }
  });
};
